/**
 * The employee portal shell.
 *
 * Deliberately shallow: a bar, three destinations, and the page. Someone
 * looking for a room in under a minute should never meet a sidebar.
 */
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Endorsement, Lockup } from "@/components/Logo";
import { Notice } from "@/components/States";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { useOnline } from "@/lib/live";
import { useSession } from "@/lib/session";
import type { NotificationRecord } from "@/lib/types";

export function PortalLayout() {
  const { user, organization, signOut, canAccessAdmin, theme, setTheme } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const online = useOnline();

  const { data: notifications } = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      api.get<{ notifications: NotificationRecord[]; unread: number }>(
        "/portal/notifications",
      ),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = notifications?.unread ?? 0;

  return (
    <div className="portal">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="portal__bar">
        <Link to="/" className="portal__brand" aria-label="AlphaRoom home">
          <Lockup height={26} />
        </Link>

        <nav className="portal__nav" aria-label="Main">
          <NavLink to="/" end>
            <Icon name="search" size={16} />
            <span>Find a room</span>
          </NavLink>
          <NavLink to="/bookings">
            <Icon name="calendar" size={16} />
            <span>My bookings</span>
          </NavLink>
        </nav>

        <div className="grow" />

        <div className="account" ref={menuRef}>
          <div className="row" style={{ gap: "var(--space-1)" }}>
            <div style={{ position: "relative" }}>
              <IconButton
                icon="bell"
                label={unread ? `Notifications, ${unread} unread` : "Notifications"}
                onClick={() => {
                  setNotificationsOpen((open) => !open);
                  setMenuOpen(false);
                  if (!notificationsOpen && unread) {
                    void api.post("/portal/notifications/read").catch(() => {});
                  }
                }}
              />
              {unread > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 4, right: 4, width: 8, height: 8,
                    borderRadius: "50%", background: "var(--accent)",
                  }}
                />
              )}
            </div>
            <button
              type="button"
              className="account__button"
              onClick={() => {
                setMenuOpen((open) => !open);
                setNotificationsOpen(false);
              }}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              // Initials are not a name. Without this the control announces as
              // two letters, which tells a screen-reader user nothing.
              aria-label="Your account"
            >
              <span className="avatar" aria-hidden="true">
                {initials(user?.display_name ?? "")}
              </span>
              <Icon name="chevronDown" size={14} />
            </button>
          </div>

          {notificationsOpen && (
            <div className="menu" style={{ minWidth: 340 }} role="region" aria-label="Notifications">
              <div className="menu__section">
                <p className="t-label">Notifications</p>
              </div>
              <div className="menu__divider" />
              {notifications?.notifications.length ? (
                notifications.notifications.slice(0, 6).map((item) => (
                  <div key={item.id} className="menu__section">
                    <p className="t-label">{item.subject}</p>
                    <p className="t-caption ink-secondary" style={{ whiteSpace: "pre-line" }}>
                      {item.body.split("\n").slice(0, 2).join(" ")}
                    </p>
                  </div>
                ))
              ) : (
                <div className="menu__section">
                  <p className="t-body ink-secondary">
                    Nothing yet. Booking updates will appear here.
                  </p>
                </div>
              )}
            </div>
          )}

          {menuOpen && (
            <div className="menu" role="menu">
              <div className="menu__section">
                <p className="t-label">{user?.display_name}</p>
                <p className="t-caption ink-tertiary">{user?.email}</p>
                <p className="t-caption ink-tertiary">{organization?.name}</p>
              </div>
              <div className="menu__divider" />
              <div className="menu__section" style={{ paddingBottom: 0 }}>
                <p className="t-caption ink-tertiary">Appearance</p>
              </div>
              <div className="menu__section" style={{ paddingTop: "var(--space-2)" }}>
                <div className="segmented" role="group" aria-label="Colour theme">
                  {(["light", "dark", "system"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={theme === option}
                      onClick={() => setTheme(option)}
                    >
                      {option === "light" ? "Light" : option === "dark" ? "Dark" : "System"}
                    </button>
                  ))}
                </div>
              </div>
              {user?.auth_provider === "local" && (
                <>
                  <div className="menu__divider" />
                  <Link className="menu__item" to="/password" role="menuitem">
                    <Icon name="key" size={16} />
                    Change password
                  </Link>
                </>
              )}
              {canAccessAdmin && (
                <>
                  <div className="menu__divider" />
                  <Link className="menu__item" to="/admin" role="menuitem">
                    <Icon name="settings" size={16} />
                    Administration
                  </Link>
                </>
              )}
              <div className="menu__divider" />
              <button
                type="button"
                className="menu__item"
                role="menuitem"
                onClick={async () => {
                  await signOut();
                  navigate("/sign-in");
                }}
              >
                <Icon name="logout" size={16} />
                Sign out
              </button>
              <div className="menu__divider" />
              <div className="menu__section">
                <Endorsement />
              </div>
            </div>
          )}
        </div>
      </header>

      {!online && (
        <div style={{ padding: "var(--space-3) var(--space-6) 0" }}>
          <Notice tone="warning" title="You are offline">
            Room availability may be out of date and new bookings cannot be saved until
            your connection returns.
          </Notice>
        </div>
      )}

      <main className="portal__main" id="main">
        <Outlet />
      </main>
    </div>
  );
}
